import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Alert, Button, InputText, Select, type SelectOption } from "@/deps/ui-library";
import { useAccounts, useCreateStatementEntry } from "@/api/queries";
import type { Account, BankAccount, StatementLineToReview } from "@/api/types";
import { useCan, useTaxWords } from "@/app/session";
import { Dialog } from "@/components/Dialog";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { statementLineSearch } from "./fromStatementLine";

type Kind = "journal" | "document";

/** Accounts an entry can go against: any but the bank's own, income or expense first as they usually are. */
export function entryAccountOptions(
  accounts: Account[] | undefined,
  bankLedgerAccountId: string,
  moneyIn: boolean,
): SelectOption[] {
  const usual = moneyIn ? "Income" : "Expenses";
  return (accounts ?? [])
    .filter((option) => option.isActive && option.id !== bankLedgerAccountId)
    .sort(
      (a, b) =>
        Number(b.group === usual) - Number(a.group === usual) || a.code.localeCompare(b.code),
    )
    .map((option) => ({ value: option.id, label: `${option.code} · ${option.name}` }));
}

/**
 * An entry for a line the books don't have yet.
 *
 * A charge or a bit of interest posts here and now, as a journal voucher against the account chosen.
 * A customer's or supplier's money needs what their form asks, such as the invoices it settles and tax
 * withheld, so it opens that form with the line's figures, and matches when it's saved.
 */
export function CreateEntryDialog({
  line,
  account,
  onClose,
}: {
  line: StatementLineToReview;
  account: BankAccount;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const can = useCan();
  const say = useTaxWords();
  const { data: accounts } = useAccounts();
  const create = useCreateStatementEntry();

  const moneyIn = Number(line.amount) > 0;
  const canJournal = can("accounting.journals.create");
  const canDocument = can(moneyIn ? "sales.receipts.create" : "purchases.payments.create");
  const documentLabel = moneyIn ? "Receipt from a customer" : "Payment to a supplier";
  const journalLabel = moneyIn ? "Other income" : "Expense or bank charge";

  const kinds: SelectOption[] = [
    ...(moneyIn && canDocument ? [{ value: "document", label: documentLabel }] : []),
    ...(canJournal ? [{ value: "journal", label: journalLabel }] : []),
    ...(!moneyIn && canDocument ? [{ value: "document", label: documentLabel }] : []),
  ];

  const [picked, setPicked] = useState<Kind | null>(null);
  const kind = picked ?? (kinds[0]?.value as Kind | undefined) ?? "journal";
  const [accountId, setAccountId] = useState("");
  const [memo, setMemo] = useState(line.description);
  const [problem, setProblem] = useState<string | null>(null);

  const options = entryAccountOptions(accounts, account.accountId, moneyIn);

  if (problem) {
    return (
      <Dialog open onClose={onClose} title="Entry posted" dismissLabel="Done">
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          description={`The entry is posted, but it wasn't matched to the line. ${problem}`}
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Create an entry for ${line.description}`}
      description={`${formatDate(line.date)} · ${formatAmount(line.amount)}`}
      footer={
        kind === "document" ? (
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            onClick={() =>
              void navigate({
                to: moneyIn ? "/sales/receipts/new" : "/purchases/payments/new",
                search: statementLineSearch(line, account.id),
              })
            }
          >
            Continue to the form
          </Button>
        ) : (
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={accountId === "" || create.isPending}
            onClick={() =>
              create.mutate(
                { lineId: line.id, accountId, memo },
                {
                  onSuccess: ({ matchProblem }) => {
                    if (matchProblem) setProblem(matchProblem);
                    else onClose();
                  },
                },
              )
            }
          >
            {create.isPending ? "Creating" : "Create and match"}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-3">
        {create.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(create.error as Error).message}
          />
        ) : null}
        <Select
          label="Transaction type"
          required
          options={kinds}
          value={kind}
          showClearButton={false}
          onChange={(details) => {
            const chosen = Array.isArray(details.value) ? details.value[0] : details.value;
            if (chosen) setPicked(String(chosen.value) as Kind);
          }}
        />
        {kind === "journal" ? (
          <>
            <Select
              label="Account"
              required
              options={options}
              value={accountId}
              showClearButton={false}
              onChange={(details) => {
                const chosen = Array.isArray(details.value) ? details.value[0] : details.value;
                if (chosen) setAccountId(String(chosen.value));
              }}
            />
            <InputText
              label="Memo"
              value={memo}
              onChange={(event) => setMemo(event.target.value)}
            />
            <p className="text-fg-muted text-xs">
              Posts a journal voucher on {formatDate(line.date)}: {account.name} against this
              account.{" "}
              {say("With VAT to claim, record a bill and pay it, so it reaches the VAT register.")}
            </p>
          </>
        ) : (
          <p className="text-fg-muted text-xs">
            Opens the form with this line&apos;s date, amount and bank account. Saving it matches
            the two.
          </p>
        )}
      </div>
    </Dialog>
  );
}
